import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import CustomEditableTable from './CustomEditableTable';
import ExcelLikeSOA from './ExcelLikeSOA';
import VersionHistoryDialog from './VersionHistoryDialog';
import scheduleOfActivitiesService from '../../services/scheduleOfActivities.service';

const stripFootnoteNumber = (text) => {
  if (!text) return '';
  return text.replace(/^\s*\d+[\.\)]\s*/, '').trim();
};

const ScheduleOfActivities = ({ data, onRegenerate, onDataUpdate, protocolId, readOnly = false }) => {
  const [promptOpen, setPromptOpen] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  //const [originalPrompt, setOriginalPrompt] = useState('Generate a standard Schedule of Activities table for a clinical trial');
  //const [combinedPrompt, setCombinedPrompt] = useState('');
  const [editTableOpen, setEditTableOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'excel'
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [keynotesDialogOpen, setKeynotesDialogOpen] = useState(false);
  const [editingKeynotesRowIndex, setEditingKeynotesRowIndex] = useState(null);
  const [editingKeynotesColIndex, setEditingKeynotesColIndex] = useState(null);
  const [editingKeynotesActivityName, setEditingKeynotesActivityName] = useState('');
  const [keynotesEditValue, setKeynotesEditValue] = useState('');
  // Helper to transform backend data to editableTableData format
  const transformBackendToTable = (backendData) => {
    if (!backendData || !backendData.visits || !backendData.sections) return [];
    
    const header = ['Study procedures and visits', ...backendData.visits.map((visit) => `${visit.period}\n${visit.day}`), 'Key Notes'];
    const footnotes = (backendData.footnotes || []).map(stripFootnoteNumber);
    let rows = [];

    // Visit windows (e.g. "+/- 3 days") live on visits[i].window — render as a pinned info
    // row right under the header, same as the editable grid in EditSoaDialog.jsx.
    if (backendData.visits.some((v) => String(v?.window || '').trim())) {
      const windowRow = {};
      header.forEach((col, i) => {
        if (i === 0) windowRow[col] = 'Visit Window';
        else if (i === header.length - 1) windowRow[col] = '';
        else windowRow[col] = backendData.visits[i - 1]?.window || '';
      });
      rows.push(windowRow);
    }

    backendData.sections.forEach(section => {
      let sectionRow = {};
      header.forEach((col, i) => {
        sectionRow[col] = i === 0 ? section.title : '';
      });
      rows.push(sectionRow);
      
      section.activities.forEach(activity => {
        let activityRow = {};
        header.forEach((col, i) => {
          if (i === 0) {
            activityRow[col] = activity.name;
          } else if (i === header.length - 1) {
            // Last column is "Keynotes" - show complete footnote text if exists
            activityRow[col] = (activity.power && stripFootnoteNumber(footnotes[activity.power - 1])) ? stripFootnoteNumber(footnotes[activity.power - 1]) : '';
          } else {
            activityRow[col] = activity.visits.includes(i - 1) ? 'X' : '';
          }
        });
        rows.push(activityRow);
      });
    });
    
    return rows;
  };

  // Add state for editable table data
  const [editableTableData, setEditableTableData] = useState(() => {
    return transformBackendToTable(data);
  });
  
  // Update editableTableData when data prop changes OR when switching views
  useEffect(() => {
    console.log('🔄 Syncing editableTableData from data prop');
    setEditableTableData(transformBackendToTable(data));
  }, [data, viewMode]); // Re-sync when switching between Table/Excel views

  // Auto-refresh data when component mounts (page load/visit)
  // ONLY refresh in Edit mode - in Canvas/readOnly mode, use the prop data to avoid version conflicts
  useEffect(() => {
    if (protocolId && !readOnly) {
      console.log('ScheduleOfActivities: Auto-refreshing from DB (Edit mode only)');
      refreshDataFromDatabase();
    }
  }, [protocolId, readOnly]); // Only run when protocolId changes (page load/change)

  // Read-only: table view only, close any open edit dialogs
  useEffect(() => {
    if (!readOnly) return;
    setViewMode('table');
    setPromptOpen(false);
    setEditTableOpen(false);
    setKeynotesDialogOpen(false);
  }, [readOnly]);
  
  // Remove spreadsheetMode, agGridMode, sheetData, agGridColumns, agGridRows, getRowStyle, and related logic
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleUpdateClick = () => {
    if (readOnly) return;
    setPromptOpen(true);
  };

  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate();
    }
  };

  const handlePromptSubmit = async () => {
    setPromptOpen(false);
    if (onRegenerate) {
      await onRegenerate(currentPrompt);
    }
    setCurrentPrompt('');
  };

  const handleShowVersionHistory = () => {
    if (readOnly) return;
    setVersionHistoryOpen(true);
  };

  const handleRollbackToVersion = async (versionNumber) => {
    if (readOnly) return;
    try {
      const response = await scheduleOfActivitiesService.rollbackToVersion(protocolId, versionNumber);
      if (response.success) {
        setSuccessMessage(`Successfully rolled back to version ${versionNumber}`);
        // Refresh data to show rolled back version
        await refreshDataFromDatabase();
        // Update parent component
        if (onDataUpdate) {
          await onDataUpdate(response.data);
        }
      }
    } catch (error) {
      console.error('Error rolling back:', error);
      setSaveError(`Failed to rollback: ${error.message}`);
    }
  };

  const handleDataChange = async (newData) => {
    if (readOnly) return;
    // Update the data when changes are made in Excel-like view
    try {
      // Validate protocolId before saving
      if (!protocolId) {
        throw new Error('Protocol ID is required. Please ensure you are working with a valid protocol.');
      }

      console.log('💾 Saving data from EDIT View:', newData);
      
      // Save the updated data to database
      await scheduleOfActivitiesService.saveScheduleOfActivities({
        protocolId,
        ...newData
      });
      
      console.log('✅ Data saved to DB, updating parent component');
      
      // Update the parent component with the new data without regenerating
      if (onDataUpdate) {
        await onDataUpdate(newData);
      }
      
      // Update the local editableTableData state for Table View
      const updatedEditableData = transformBackendToTable(newData);
      setEditableTableData(updatedEditableData);
      
      console.log('✅ Table View data synced');
    } catch (error) {
      console.error('Error updating data:', error);
    }
  };

  // Function to refresh data from database
  const refreshDataFromDatabase = async () => {
    try {
      const response = await scheduleOfActivitiesService.getScheduleOfActivities(protocolId);
      if (response && response.success && response.data) {
        const doc = response.data != null && response.data.data !== undefined ? response.data.data : response.data;
        // Update the parent component with fresh data
        if (onDataUpdate) {
          await onDataUpdate(doc);
        }
      }
    } catch (error) {
      console.error('Error refreshing data from database:', error);
    }
  };

  // Helper to transform editableTableData back to backend format
  const transformTableToBackend = (tableRows) => {
    if (!tableRows || tableRows.length === 0) return { visits: [], sections: [], footnotes: [] };
    const header = Object.keys(tableRows[0]);
    // Remove "Keynotes" column from header when extracting visits
    const visitHeaders = header.filter(h => h !== 'Key Notes' && h !== 'Keynotes' && h !== 'Study procedures and visits');
    const visits = visitHeaders.map(h => {
      const [period, day] = h.split('\n');
      return { period: period || '', day: day || '' };
    });
    let sections = [];
    let currentSection = null;
    let footnotes = [...(data.footnotes || [])].map(stripFootnoteNumber);  // Start with existing footnotes
    const usedFootnotes = new Set(); // Track which footnotes are actually used
    
    tableRows.forEach(row => {
      const firstCol = row[header[0]];
      const isSection = firstCol && Object.values(row).slice(1).every(v => v === '');
      if (isSection) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: firstCol, activities: [] };
      } else if (currentSection) {
        // Activity row
        const keynotesValue = stripFootnoteNumber((row['Key Notes'] || row['Keynotes'] || '').trim());
        let power = undefined;
        
        if (keynotesValue) {
          // Try to find existing footnote
          let footnoteIndex = footnotes.findIndex(fn => fn === keynotesValue);
          
          if (footnoteIndex === -1) {
            // New footnote - add it to the array
            footnotes.push(keynotesValue);
            footnoteIndex = footnotes.length - 1;
          }
          
          power = footnoteIndex + 1; // Footnotes are 1-indexed
          usedFootnotes.add(footnoteIndex);
        }
        
        const activity = {
          name: firstCol,
          visits: visitHeaders.map((h, idx) => row[h] === 'X' ? idx : null).filter(idx => idx !== null),
          power: power || undefined
        };
        currentSection.activities.push(activity);
      }
    });
    if (currentSection) sections.push(currentSection);
    return { visits, sections, footnotes };
  };

  const handleSaveTable = async () => {
    if (readOnly) return;
    setSaveLoading(true);
    setSaveError('');
    try {
      // Validate protocolId before saving
      if (!protocolId) {
        throw new Error('Protocol ID is required. Please ensure you are working with a valid protocol.');
      }

      const { visits, sections, footnotes } = transformTableToBackend(editableTableData);
      await scheduleOfActivitiesService.saveScheduleOfActivities({
        protocolId,
        visits,
        sections,
        footnotes: footnotes || [],
        summary: data.summary || ''
      });
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err.message || 'Failed to save table');
    } finally {
      setSaveLoading(false);
    }
  };

  //const tableCellStyle = {
    //border: '1px solid rgba(224, 224, 224, 1)',
    //padding: '8px',
  //};

  // Export handlers
  const handleExportMenuOpen = (event) => {
    if (readOnly) return;
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportM11XML = async () => {
    if (readOnly) return;
    try {
      setExportLoading(true);
      setExportMenuAnchor(null);
      
      const response = await scheduleOfActivitiesService.exportToM11XML(protocolId);
      
      // Create blob and download
      const blob = new Blob([response], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SoA_M11_${protocolId}_v${data?.versionNumber || 1}_${Date.now()}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('M11 XML exported successfully!');
      setExportLoading(false);
    } catch (error) {
      console.error('Error exporting M11 XML:', error);
      setSaveError('Failed to export M11 XML: ' + (error.response?.data?.message || error.message));
      setExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (readOnly) return;
    try {
      setExportLoading(true);
      setExportMenuAnchor(null);
      
      // Use existing Excel export if available
      const response = await scheduleOfActivitiesService.exportToExcel(protocolId);
      
      // Create blob and download
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SoA_${protocolId}_v${data?.versionNumber || 1}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Excel file exported successfully!');
      setExportLoading(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setSaveError('Failed to export Excel: ' + (error.response?.data?.message || error.message));
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            Schedule of Activities
          </Typography>
          {data?.versionNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip 
                label={`Version ${data.versionNumber}`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              {data.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(data.createdAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!readOnly ? (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">Table View</ToggleButton>
              <ToggleButton value="excel">EDIT View</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Chip label="View only" size="small" variant="outlined" />
          )}
          {!readOnly && (
            <>
          <Button
            variant="outlined"
            color="info"
            onClick={handleShowVersionHistory}
            startIcon={<HistoryIcon />}
          >
            Version History
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleExportMenuOpen}
            startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
            disabled={exportLoading}
          >
            {exportLoading ? 'Exporting...' : 'Export'}
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleExportM11XML}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Export as M11 XML
            </MenuItem>
            <MenuItem onClick={handleExportExcel}>
              <FileDownloadIcon sx={{ mr: 1 }} />
              Export as Excel
            </MenuItem>
          </Menu>
            </>
          )}
          {!readOnly && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleUpdateClick}
          >
            Edit Table with Prompt
          </Button>
          )}
          {/* <Button 
            variant="contained" 
            color="primary" 
            onClick={handleRegenerate}
          >
            Regenerate Table
          </Button> */}
          {/* <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditTableOpen(true)}
          >
            Custom Edit Table
          </Button> */}
        </Box>
      </Box>

      {!readOnly && viewMode === 'excel' ? (
        <ExcelLikeSOA
          data={data}
          protocolId={protocolId}
          onDataChange={handleDataChange}
          readOnly={readOnly}
        />
      ) : (
        <>
          {/* Render the main table from editableTableData */}
          <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: 'max-content', tableLayout: 'auto', width: 'auto' }}>
              <TableHead>
                <TableRow>
                  {editableTableData.length > 0 && Object.keys(editableTableData[0]).map((col, idx, arr) => {
                    const isFirstCol = idx === 0;
                    const isKeynotesCol = col === 'Key Notes' || col === 'Keynotes';
                    return (
                    <TableCell key={idx} sx={{ 
                      border: '1px solid rgba(224, 224, 224, 1)', 
                      padding: '8px', 
                      whiteSpace: isKeynotesCol ? 'normal' : 'pre-line',
                      fontWeight: isFirstCol ? 'bold' : undefined, 
                      width: isFirstCol ? '25%' : undefined,
                      minWidth: isKeynotesCol ? '250px' : undefined,
                      maxWidth: isKeynotesCol ? '400px' : undefined
                    }}>
                      {col}
                    </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {editableTableData.map((row, rowIdx) => {
                  const headerKeys = editableTableData.length > 0 ? Object.keys(editableTableData[0]) : [];
                  const isSectionRow = rowIdx === 0 || (row[headerKeys[0]] && Object.values(row).slice(1).every(v => v === ''));
                  const isActivityRow = !isSectionRow;
                  
                  return (
                    <TableRow key={rowIdx}>
                      {Object.keys(row).map((col, colIdx) => {
                        const isKeynotesColumn = col === 'Key Notes' || col === 'Keynotes';
                        return (
                          <TableCell 
                            key={colIdx} 
                            sx={{ 
                              border: '1px solid rgba(224, 224, 224, 1)', 
                              padding: '8px',
                              whiteSpace: isKeynotesColumn ? 'normal' : undefined,
                              backgroundColor: isSectionRow ? '#f5f5f5' : undefined, 
                              fontWeight: isSectionRow ? 'bold' : undefined,
                              cursor: !readOnly && isActivityRow && isKeynotesColumn ? 'pointer' : 'default',
                              minWidth: isKeynotesColumn ? '250px' : undefined,
                              maxWidth: isKeynotesColumn ? '400px' : undefined,
                              '&:hover': !readOnly && isActivityRow && isKeynotesColumn ? { backgroundColor: '#f0f0f0' } : {}
                            }}
                            onClick={() => {
                              if (readOnly) return;
                              if (isActivityRow && isKeynotesColumn) {
                                setEditingKeynotesRowIndex(rowIdx);
                                setEditingKeynotesColIndex(colIdx);
                                setEditingKeynotesActivityName(row[headerKeys[0]] || '');
                                setKeynotesEditValue(row[col] || '');
                                setKeynotesDialogOpen(true);
                              }
                            }}
                          >
                            {row[col]}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Prompt dialog remains unchanged */}
      <Dialog open={promptOpen} onClose={() => setPromptOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Schedule of Activities</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Enter your requirements or modifications:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={currentPrompt}
              onChange={(e) => setCurrentPrompt(e.target.value)}
              variant="outlined"
              placeholder="Enter any specific requirements or modifications for the Schedule of Activities..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptOpen(false)}>Cancel</Button>
          <Button onClick={handlePromptSubmit} variant="contained" color="primary">
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editTableOpen} onClose={() => setEditTableOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Custom Editable Table</DialogTitle>
        <DialogContent>
          {/* Build the complete table data for CustomEditableTable */}
          <CustomEditableTable initialData={editableTableData} onTableChange={setEditableTableData} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTableOpen(false)} color="primary">Close</Button>
          <Button onClick={handleSaveTable} color="success" variant="contained" disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Keynotes Edit Dialog */}
      <Dialog 
        open={keynotesDialogOpen} 
        onClose={() => {
          setKeynotesDialogOpen(false);
          setEditingKeynotesRowIndex(null);
          setEditingKeynotesColIndex(null);
          setEditingKeynotesActivityName('');
          setKeynotesEditValue('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Key Notes</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Activity: <strong>{editingKeynotesActivityName}</strong>
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={6}
              maxRows={12}
              label="Keynotes (Footnotes)"
              value={keynotesEditValue}
              onChange={(e) => setKeynotesEditValue(e.target.value)}
              placeholder="Enter footnote text here..."
              variant="outlined"
              sx={{ mt: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This text will appear in the Keynotes column and will be saved as a footnote reference.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setKeynotesDialogOpen(false);
            setEditingKeynotesRowIndex(null);
            setEditingKeynotesColIndex(null);
            setEditingKeynotesActivityName('');
            setKeynotesEditValue('');
          }}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (editingKeynotesRowIndex !== null && editingKeynotesColIndex !== null) {
                const updatedData = [...editableTableData];
                const headerKeys = Object.keys(updatedData[0]);
                const keynotesKey = headerKeys[editingKeynotesColIndex];
                updatedData[editingKeynotesRowIndex][keynotesKey] = keynotesEditValue;
                setEditableTableData(updatedData);
                
                // ✅ CRITICAL FIX: Save to database immediately
                try {
                  console.log('💾 Saving keynotes to database...');
                  const { visits, sections, footnotes } = transformTableToBackend(updatedData);
                  await scheduleOfActivitiesService.saveScheduleOfActivities({
                    protocolId,
                    visits,
                    sections,
                    footnotes: footnotes || [],
                    summary: data.summary || ''
                  });
                  
                  // Update parent component
                  if (onDataUpdate) {
                    await onDataUpdate({ visits, sections, footnotes, summary: data.summary });
                  }
                  
                  console.log('✅ Keynotes saved to database successfully');
                  setSaveSuccess(true);
                  setTimeout(() => setSaveSuccess(false), 3000);
                } catch (error) {
                  console.error('❌ Error saving keynotes:', error);
                  setSaveError('Failed to save keynotes: ' + error.message);
                }
              }
              setKeynotesDialogOpen(false);
              setEditingKeynotesRowIndex(null);
              setEditingKeynotesColIndex(null);
              setEditingKeynotesActivityName('');
              setKeynotesEditValue('');
            }} 
            variant="contained" 
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)}>
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Table saved successfully!
        </Alert>
      </Snackbar>
      <Snackbar open={!!saveError} autoHideDuration={4000} onClose={() => setSaveError('')}>
        <Alert onClose={() => setSaveError('')} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
      <Snackbar open={successMessage !== ''} autoHideDuration={6000} onClose={() => setSuccessMessage('')}>
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {viewMode === 'table' && (
        <>
          {data.summary && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Summary:
              </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            value={data.summary}
            variant="outlined"
            InputProps={{
              readOnly: true,
              sx: {
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              },
            }}
          />
            </Box>
          )}
        </>
      )}
      
      {!readOnly && (
      <VersionHistoryDialog
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        protocolId={protocolId}
        onRollback={handleRollbackToVersion}
        allowRollback={!readOnly}
      />
      )}
    </Box>
  );
};

export default ScheduleOfActivities; 